// Journal (collection) state, shared by the shelf, the detail page, and the composer.
//
// Modelled on EntriesProvider: the server is the source of truth, rows are decrypted in memory, and
// every mutation goes to the server first and updates local state from what came back. Loaded lazily
// — someone who never opens the Journals tab should not pay for it — and only once per session
// unless something changes it.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useSession } from '../auth/SessionProvider';
import {
  createCollection,
  deleteCollection,
  fetchCollections,
  removeCollectionEntry,
  setCollectionEntries,
  updateCollection,
  type Collection,
  type CollectionDraft,
  type CollectionPatch,
} from '../api/collections';

interface JournalsValue {
  collections: Collection[];
  isLoading: boolean;
  error: string | null;
  refresh(): Promise<void>;
  byId(id: string): Collection | undefined;
  /** How many journals an entry appears in — the "in N journals" badge, counted locally. */
  countFor(entryId: string): number;
  create(draft: CollectionDraft): Promise<Collection>;
  update(id: string, patch: CollectionPatch): Promise<Collection>;
  remove(id: string): Promise<void>;
  setEntries(id: string, entryIds: string[]): Promise<void>;
  removeEntry(id: string, entryId: string): Promise<void>;
  /** Put one entry into a journal without disturbing the rest — used when writing straight into it. */
  addEntry(id: string, entryId: string): Promise<void>;
}

const JournalsContext = createContext<JournalsValue | null>(null);

export function useJournals(): JournalsValue {
  const ctx = useContext(JournalsContext);
  if (!ctx) throw new Error('useJournals must be used inside <JournalsProvider>');
  return ctx;
}

export function JournalsProvider({ children }: { children: ReactNode }) {
  const { vault } = useSession();
  const dek = vault.status === 'unlocked' ? vault.dek : null;

  const [collections, setCollections] = useState<Collection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dekRef = useRef<CryptoKey | null>(null);
  dekRef.current = dek;

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!dek) return;
      setError(null);
      try {
        const fetched = await fetchCollections(dek, signal);
        if (signal?.aborted) return;
        setCollections(fetched);
      } catch (err) {
        if (signal?.aborted) return;
        setError(err instanceof Error ? err.message : 'Could not load your journals.');
      } finally {
        if (!signal?.aborted) setIsLoading(false);
      }
    },
    [dek],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const requireKey = () => {
    const key = dekRef.current;
    if (!key) throw new Error('Your vault is locked.');
    return key;
  };

  const put = (next: Collection) =>
    setCollections((current) => {
      const known = current.some((c) => c.id === next.id);
      return known ? current.map((c) => (c.id === next.id ? next : c)) : [next, ...current];
    });

  const create = useCallback(async (draft: CollectionDraft) => {
    const made = await createCollection(requireKey(), draft);
    put(made);
    return made;
  }, []);

  const update = useCallback(async (id: string, patch: CollectionPatch) => {
    const saved = await updateCollection(requireKey(), id, patch);
    // The PATCH response carries the journal's own columns but not its membership, which this call
    // never touched — so the ids already in memory are the ones to keep.
    setCollections((current) =>
      current.map((c) => (c.id === id ? { ...saved, entryIds: c.entryIds } : c)),
    );
    return saved;
  }, []);

  const remove = useCallback(async (id: string) => {
    await deleteCollection(id);
    setCollections((current) => current.filter((c) => c.id !== id));
  }, []);

  const setEntries = useCallback(async (id: string, entryIds: string[]) => {
    // The server answers with what it kept, which is not always what was asked for: an id that is
    // not a live entry of this user's is dropped rather than filed.
    const kept = await setCollectionEntries(id, entryIds);
    setCollections((current) =>
      current.map((c) => (c.id === id ? { ...c, entryIds: kept } : c)),
    );
  }, []);

  const removeEntry = useCallback(async (id: string, entryId: string) => {
    await removeCollectionEntry(id, entryId);
    setCollections((current) =>
      current.map((c) =>
        c.id === id ? { ...c, entryIds: c.entryIds.filter((e) => e !== entryId) } : c,
      ),
    );
  }, []);

  const addEntry = useCallback(
    async (id: string, entryId: string) => {
      const journal = collections.find((c) => c.id === id);
      if (!journal || journal.entryIds.includes(entryId)) return;
      await setEntries(id, [...journal.entryIds, entryId]);
    },
    [collections, setEntries],
  );

  const byId = useCallback((id: string) => collections.find((c) => c.id === id), [collections]);

  const countFor = useCallback(
    (entryId: string) => collections.filter((c) => c.entryIds.includes(entryId)).length,
    [collections],
  );

  const value = useMemo<JournalsValue>(
    () => ({
      collections,
      isLoading,
      error,
      refresh: () => load(),
      byId,
      countFor,
      create,
      update,
      remove,
      setEntries,
      removeEntry,
      addEntry,
    }),
    [
      collections,
      isLoading,
      error,
      load,
      byId,
      countFor,
      create,
      update,
      remove,
      setEntries,
      removeEntry,
      addEntry,
    ],
  );

  return <JournalsContext.Provider value={value}>{children}</JournalsContext.Provider>;
}
